import { Type } from 'class-transformer';
import {
  ArrayUnique,
  IsArray,
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  ValidateNested,
} from 'class-validator';
import { MicronutrientInputDto } from './micronutrient-input.dto';
import {
  NUTRITIONAL_PROFILE_METHODS,
  type NutritionalProfileMethod,
} from './nutritional-profile-method.enum';

/**
 * Per-100g nutritional profile (Allegato XV Reg. UE 1169/2011). All masses are
 * in grams. Required macro fields + salt are non-negative; the optional ones
 * (fiber/sodium/alcohol/polyols/organicAcids) are declared only when present on
 * the source. Salt is declared, never inferred from sodium here.
 *
 * `baseQuantityG` is fixed at 100 in T1 (enforced per-property); it stays in the
 * payload so a future different base needs no DTO change. The cross-field rules
 * `saturatedFat<=fat` and `sugars<=carbohydrate` are enforced by the nutrition
 * service (`_assertProfileShape`), which guards both the HTTP and admin paths.
 *
 * `micronutrients` is an optional additive field (Allegato XIII): its absence
 * keeps every existing caller of this DTO backward-compatible.
 */
export class NutritionalProfileInputDto {
  @IsInt()
  @Min(100)
  @Max(100)
  baseQuantityG = 100;

  @IsNumber()
  @Min(0)
  @Max(99_999_999.99)
  energyKj!: number;

  @IsNumber()
  @Min(0)
  @Max(99_999_999.99)
  energyKcal!: number;

  @IsNumber()
  @Min(0)
  @Max(100)
  fat!: number;

  @IsNumber()
  @Min(0)
  @Max(100)
  saturatedFat!: number;

  @IsNumber()
  @Min(0)
  @Max(100)
  carbohydrate!: number;

  @IsNumber()
  @Min(0)
  @Max(100)
  sugars!: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  fiber?: number | null;

  @IsNumber()
  @Min(0)
  @Max(100)
  protein!: number;

  @IsNumber()
  @Min(0)
  @Max(100)
  salt!: number;

  /**
   * Sodium is declared in grams and salt is derived from it as sodium x 2,5,
   * so the ceiling is not 100: above 40 g the derived salt would weigh more
   * than the food that contains it. A label once printed "Sale: 852,0 g" per
   * 100 g because a milligram figure was typed into this gram field, and a
   * 100 g cap let it through.
   */
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(40)
  sodium?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  alcohol?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  polyols?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  organicAcids?: number | null;

  /**
   * All. VII Parte B punto 17 Reg. (UE) 1169/2011: rapporto collagene/proteine
   * di carne, in percentuale. Non e' un nutriente dichiarabile in etichetta:
   * serve solo a decidere se una designazione generica «carni di ...» resta
   * ammessa. Assente = non misurato (mai zero implicito).
   */
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  collagenToMeatProteinRatioPercent?: number | null;

  @IsEnum(NUTRITIONAL_PROFILE_METHODS)
  method!: NutritionalProfileMethod;

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  sourceReference?: string | null;

  @IsOptional()
  @IsUUID()
  sourceDocumentId?: string | null;

  /**
   * Vitamine dichiarate (Allegato XIII Reg. UE 1169/2011). Un codice assente
   * significa "non rilevato/non dichiarato", mai zero implicito; l'array puo'
   * essere vuoto o omesso senza che questo implichi valori nulli per legge.
   */
  @IsOptional()
  @IsArray()
  @ArrayUnique((entry: MicronutrientInputDto) => entry.nutrientCode)
  @ValidateNested({ each: true })
  @Type(() => MicronutrientInputDto)
  micronutrients?: MicronutrientInputDto[];
}
